import { useEffect, useRef, useState } from 'react';

/**
 * How long a typed FILTER on an administration screen settles before it is asked about.
 *
 * Longer than the register's search box, because these are dates and identifiers rather than a
 * name being looked up while somebody waits: a date typed digit by digit is a valid, different date
 * after every keystroke, and on the incident log every one of those requests files an audit row.
 */
export const FILTER_SETTLE_MS = 400;

/**
 * A value as it was a moment after it last changed.
 *
 * Two screens in this feature ask the server about something an operator is still typing — the
 * duplicate guard and the register's search box — and both would otherwise issue a request per
 * keystroke. That is not only wasteful: the duplicate guard's answer arrives as a warning, and a
 * warning that flickers on every letter is one people stop reading.
 *
 * **It compares by VALUE, not by identity.** The duplicate guard's input is a freshly built object
 * on every render, so an identity comparison would restart the timer forever and the debounced value
 * would never settle. Serialising is cheap here — the map is at most a few dozen short fields — and
 * it is what makes "the operator stopped typing" mean what it says.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  const signature = JSON.stringify(value);
  const latest = useRef(value);

  // Written in an EFFECT rather than during render, because a ref is not part of what a render
  // produces: React may render a component without committing it, and a ref updated in that render
  // would then describe a screen nobody ever saw. This runs after every commit, so what the timer
  // below reads is what is on screen.
  useEffect(() => {
    latest.current = value;
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(latest.current);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
    // Keyed on the signature rather than on `value`: see the module note. The value itself is read
    // through the ref when the timer fires, which is outside render and after the last commit.
  }, [delayMs, signature]);

  return settled;
}
